import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { SafeNameInput } from '../ui/safe-name-input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Building } from 'lucide-react';

export const VenueDialog = ({ open, onOpenChange, venue, sections, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    sectionIds: []
  });

  useEffect(() => {
    if (venue) {
      setFormData({
        name: venue.name || '',
        sectionIds: venue.sectionIds || []
      });
    } else {
      setFormData({
        name: '',
        sectionIds: []
      });
    }
  }, [venue, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleSectionToggle = (sectionId) => {
    setFormData(prev => {
      const isSelected = prev.sectionIds.includes(sectionId);
      return {
        ...prev,
        sectionIds: isSelected
          ? prev.sectionIds.filter(id => id !== sectionId)
          : [...prev.sectionIds, sectionId]
      };
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Building className="w-5 h-5 text-teal-600" />
            <span>{venue ? 'Edit Venue' : 'Add Venue'}</span>
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Venue Name */}
          <div>
            <Label htmlFor="name">Venue Name *</Label>
            <SafeNameInput
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter venue name"
              required
            />
          </div>

          {/* Sections Selection */}
          <div>
            <Label className="mb-3 block">Sections</Label>
            <p className="text-sm text-slate-600 mb-3">
              Select sections to include in this venue
            </p>
            
            {sections.length === 0 ? (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-center">
                <p className="text-sm text-slate-600">No sections available</p>
                <p className="text-xs text-slate-500 mt-1">Create sections first to assign them to venues</p>
              </div>
            ) : (
              <div className="border border-slate-200 rounded-lg max-h-64 overflow-y-auto">
                {sections.map((section) => (
                  <div 
                    key={section.id}
                    className="flex items-center space-x-3 p-3 hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
                  >
                    <Checkbox
                      id={`section-${section.id}`}
                      checked={formData.sectionIds.includes(section.id)}
                      onCheckedChange={() => handleSectionToggle(section.id)}
                    />
                    <label
                      htmlFor={`section-${section.id}`}
                      className="flex-1 cursor-pointer"
                    >
                      <div className="font-medium text-slate-800">{section.name}</div>
                      <div className="text-sm text-slate-500">
                        {section.seatIds?.length || 0} seats
                      </div>
                    </label>
                  </div>
                ))}
              </div>
            )}
            
            <p className="text-xs text-slate-500 mt-2">
              {formData.sectionIds.length} section(s) selected
            </p>
          </div>

          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button 
              type="submit"
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              {venue ? 'Update Venue' : 'Create Venue'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
